import React, { useState, useEffect } from 'react';
import { Booking } from '../../types/booking';
import { Vehicle } from '../../types/vehicle';
import { bookingService } from '../../services/bookingService';
import { vehicleService } from '../../services/vehicleService';

interface ConflictResolutionModalProps {
  booking: Booking;
  conflictingBookings: Booking[];
  onResolve: (resolution: ConflictResolution) => void;
  onCancel: () => void;
  isOpen: boolean;
}

interface ConflictResolution {
  type: 'change-vehicle' | 'change-dates' | 'cancel-booking' | 'override-conflict';
  newVehicleId?: string;
  newPickUpDate?: Date;
  newReturnDate?: Date;
  reason: string;
  resolvedBy: string;
}

interface AlternativeVehicle extends Vehicle {
  isAvailable: boolean;
  priceDifference?: number;
}

const ConflictResolutionModal: React.FC<ConflictResolutionModalProps> = ({
  booking,
  conflictingBookings,
  onResolve,
  onCancel,
  isOpen
}) => {
  const [resolutionType, setResolutionType] = useState<ConflictResolution['type']>('change-vehicle');
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>('');
  const [newPickUpDate, setNewPickUpDate] = useState<string>('');
  const [newReturnDate, setNewReturnDate] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [alternativeVehicles, setAlternativeVehicles] = useState<AlternativeVehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadAlternativeVehicles();
      // Set default dates
      setNewPickUpDate(new Date(booking.pickUpDate).toISOString().split('T')[0]);
      setNewReturnDate(new Date(booking.returnDate).toISOString().split('T')[0]);
    }
  }, [isOpen, booking]);

  const loadAlternativeVehicles = async () => {
    try {
      setLoading(true);
      const response = await vehicleService.getAllVehicles();
      
      if (response.success && response.data) {
        // Filter out the current vehicle and check availability
        const alternatives = response.data
          .filter(vehicle => vehicle._id !== booking.car._id)
          .map(vehicle => ({
            ...vehicle,
            isAvailable: true, // This would be determined by checking conflicts
            priceDifference: vehicle.defaultRatePerDay - booking.car.defaultRatePerDay
          }));
        
        // Check availability for each alternative vehicle
        const availabilityChecks = await Promise.all(
          alternatives.map(async (vehicle) => {
            try {
              const conflictResponse = await bookingService.checkBookingConflict(
                vehicle._id,
                new Date(booking.pickUpDate),
                new Date(booking.returnDate)
              );
              return {
                ...vehicle,
                isAvailable: !conflictResponse.data
              };
            } catch {
              return { ...vehicle, isAvailable: false };
            }
          })
        );
        
        setAlternativeVehicles(availabilityChecks);
      }
    } catch (err) {
      setError('Failed to load alternative vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!reason.trim()) {
      setError('Please provide a reason for the resolution');
      return;
    }

    const resolution: ConflictResolution = {
      type: resolutionType,
      reason: reason.trim(),
      resolvedBy: 'admin' // This should come from auth context
    };

    if (resolutionType === 'change-vehicle' && selectedVehicleId) {
      resolution.newVehicleId = selectedVehicleId;
    }

    if (resolutionType === 'change-dates') {
      if (!newPickUpDate || !newReturnDate) {
        setError('Please select new dates');
        return;
      }
      resolution.newPickUpDate = new Date(newPickUpDate);
      resolution.newReturnDate = new Date(newReturnDate);
    }

    onResolve(resolution);
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Resolve Booking Conflict
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Booking {booking.bookingReference} has conflicts that need resolution
          </p>
        </div>

        <div className="p-6">
          {/* Conflict Details */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Conflict Details</h3>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-red-800">
                    Vehicle {booking.car.name} is double-booked
                  </h4>
                  <div className="mt-2 text-sm text-red-700">
                    <p>Current booking: {formatDate(booking.pickUpDate)} - {formatDate(booking.returnDate)}</p>
                    <p className="mt-1">Conflicting with:</p>
                    <ul className="mt-1 list-disc list-inside">
                      {conflictingBookings.map(conflict => (
                        <li key={conflict._id}>
                          Booking {conflict.bookingReference}: {formatDate(conflict.pickUpDate)} - {formatDate(conflict.returnDate)}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Resolution Type Selection */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Resolution Options</h3>
              <div className="space-y-3">
                <label className="flex items-start">
                  <input
                    type="radio"
                    name="resolutionType"
                    value="change-vehicle"
                    checked={resolutionType === 'change-vehicle'}
                    onChange={(e) => setResolutionType(e.target.value as ConflictResolution['type'])}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-900">
                      Change Vehicle
                    </div>
                    <div className="text-sm text-gray-500">
                      Assign a different available vehicle to this booking
                    </div>
                  </div>
                </label>

                <label className="flex items-start">
                  <input
                    type="radio"
                    name="resolutionType"
                    value="change-dates"
                    checked={resolutionType === 'change-dates'}
                    onChange={(e) => setResolutionType(e.target.value as ConflictResolution['type'])}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-900">
                      Change Dates
                    </div>
                    <div className="text-sm text-gray-500">
                      Modify the booking dates to avoid conflicts
                    </div>
                  </div>
                </label>

                <label className="flex items-start">
                  <input
                    type="radio"
                    name="resolutionType"
                    value="cancel-booking"
                    checked={resolutionType === 'cancel-booking'}
                    onChange={(e) => setResolutionType(e.target.value as ConflictResolution['type'])}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-900">
                      Cancel Booking
                    </div>
                    <div className="text-sm text-gray-500">
                      Cancel this booking and process refund if applicable
                    </div>
                  </div>
                </label>

                <label className="flex items-start">
                  <input
                    type="radio"
                    name="resolutionType"
                    value="override-conflict"
                    checked={resolutionType === 'override-conflict'}
                    onChange={(e) => setResolutionType(e.target.value as ConflictResolution['type'])}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-900">
                      Override Conflict
                    </div>
                    <div className="text-sm text-gray-500">
                      Mark conflict as resolved without changes (use with caution)
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Vehicle Selection */}
            {resolutionType === 'change-vehicle' && (
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Select Alternative Vehicle</h4>
                {loading ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600">Loading vehicles...</span>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {alternativeVehicles.map(vehicle => (
                      <label
                        key={vehicle._id}
                        className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                          !vehicle.isAvailable 
                            ? 'bg-gray-50 border-gray-200 cursor-not-allowed' 
                            : selectedVehicleId === vehicle._id
                            ? 'bg-blue-50 border-blue-300'
                            : 'bg-white border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="selectedVehicle"
                          value={vehicle._id}
                          checked={selectedVehicleId === vehicle._id}
                          onChange={(e) => setSelectedVehicleId(e.target.value)}
                          disabled={!vehicle.isAvailable}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {vehicle.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {vehicle.brand} {vehicle.model} ({vehicle.year})
                              </div>
                              <div className="text-sm text-gray-500">
                                {formatCurrency(vehicle.defaultRatePerDay)}/day
                                {vehicle.priceDifference !== undefined && vehicle.priceDifference !== 0 && (
                                  <span className={`ml-2 ${vehicle.priceDifference > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    ({vehicle.priceDifference > 0 ? '+' : ''}{formatCurrency(vehicle.priceDifference)} difference)
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              {vehicle.isAvailable ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Available
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  Unavailable
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}
                    {alternativeVehicles.length === 0 && (
                      <div className="text-center py-4 text-gray-500">
                        No alternative vehicles available for the selected dates
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Date Selection */}
            {resolutionType === 'change-dates' && (
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Select New Dates</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Pickup Date
                    </label>
                    <input
                      type="date"
                      value={newPickUpDate}
                      onChange={(e) => setNewPickUpDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Return Date
                    </label>
                    <input
                      type="date"
                      value={newReturnDate}
                      onChange={(e) => setNewReturnDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Resolution Reason *
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Explain why this resolution was chosen and any customer communication..."
                required
              />
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <div className="text-red-800 text-sm">{error}</div>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Resolve Conflict
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConflictResolutionModal;